import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Calendar, Search, X, ClipboardCheck, MapPin, Phone, Wrench,
  CheckCircle2, ListChecks, PlayCircle, ChevronRight, Loader2,
  Ban, UserX, IndianRupee,
} from 'lucide-react';
import { serviceOpsApi, citiesApi, serviceLeadsApi } from '@/services/api';
import { ExpandedDetail, STATUS_BADGE as LEAD_STATUS_BADGE, renderStatus } from './ServiceLeadsPage';
import ServiceOpsDetailModal from '../components/serviceOps/ServiceOpsDetailModal';
import CancelledRefundsTable from '../components/serviceOps/CancelledRefundsTable';
import AddReasonDialog from '../components/serviceOps/AddReasonDialog';
import {
  useStatusCatalogue, statusesIn, statusBadge, statusLabel, TONE_SOLID,
} from '../components/serviceOps/useStatusCatalogue';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrencyAuto } from '@/utils/currency';

// ─── Constants ────────────────────────────────────────────────────────────
const STATUS_BADGE = {
  BOOKED:             'bg-emerald-100 text-emerald-700 border-emerald-200',
  ASSIGNED:           'bg-cyan-100 text-cyan-700 border-cyan-200',
  IN_PROGRESS:        'bg-indigo-100 text-indigo-700 border-indigo-200',
  // Amber so a rejected lead jumps out of the queue at a glance — same
  // colour family the in-modal banner uses.
  MECHANIC_REJECTED:  'bg-amber-100 text-amber-800 border-amber-300',
  COMPLETED:          'bg-green-200 text-green-800 border-green-300',
  CANCELLED:          'bg-rose-100 text-rose-700 border-rose-200',
};
const STATUS_LABEL = {
  BOOKED: 'BOOKED',
  ASSIGNED: 'ASSIGNED',
  IN_PROGRESS: 'IN PROGRESS',
  MECHANIC_REJECTED: 'REJECTED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
};

const OPS_STAT_CARDS = [
  { key: '',            label: 'All Active',  icon: ListChecks,    bg: 'bg-violet-500' },
  { key: 'BOOKED',      label: 'Booked',      icon: ClipboardCheck, bg: 'bg-emerald-500' },
  { key: 'ASSIGNED',    label: 'Assigned',    icon: Wrench,        bg: 'bg-cyan-500' },
  { key: 'IN_PROGRESS', label: 'In Progress', icon: PlayCircle,    bg: 'bg-indigo-500' },
  { key: 'COMPLETED',   label: 'Completed',   icon: CheckCircle2,  bg: 'bg-green-500' },
];

// Icon per status for the KPI cards — labels, colours and WHICH statuses get a
// card all come from the backend catalogue.
const CARD_ICONS = {
  BOOKED: ClipboardCheck, ASSIGNED: Wrench, IN_PROGRESS: PlayCircle,
  COMPLETED: CheckCircle2, CANCELLED_BY_CUSTOMER: UserX, CANCELLED_BY_WD: Ban,
  MECHANIC_REJECTED: Wrench, REFUND_REQUESTED: IndianRupee,
};

const STATUS_FILTER_OPTIONS = [
  { key: 'BOOKED',             label: 'Booked' },
  { key: 'ASSIGNED',           label: 'Assigned' },
  { key: 'IN_PROGRESS',        label: 'In Progress' },
  { key: 'MECHANIC_REJECTED',  label: 'Rejected by Mechanic' },
  { key: 'COMPLETED',          label: 'Completed' },
];

const DATE_PRESETS = [
  { key: 'today',      label: 'Today' },
  { key: 'yesterday',  label: 'Yesterday' },
  { key: 'last_7',     label: 'Last 7 Days' },
  { key: 'last_14',    label: 'Last 14 Days' },
  { key: 'this_week',  label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom',     label: 'Custom' },
  { key: 'all',        label: 'All Time' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────
const fmtINR = (v) => v == null ? '—' : formatCurrencyAuto(Math.round(Number(v)));

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const fmtSlot = (s) => {
  if (!s) return '—';
  if (typeof s === 'string' && s.includes('_')) {
    return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  return fmtDateTime(s);
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Allowed transitions in the ops lifecycle.
const NEXT_STATUS = {
  BOOKED:      [],   // requires mechanic assignment via dropdown — no direct advance
  ASSIGNED:    ['IN_PROGRESS', 'CANCELLED'],
  IN_PROGRESS: ['COMPLETED', 'CANCELLED'],
  COMPLETED:   [],
};

const NEXT_LABEL = {
  IN_PROGRESS: 'Start',
  COMPLETED:   'Complete',
  CANCELLED:   'Cancel',
};

// ─── Page ─────────────────────────────────────────────────────────────────
export default function ServiceOpsPage() {
  const { user } = useAuth();
  const catalogue = useStatusCatalogue();
  // 'active' = the working queue · 'closed' = Cancelled & Refunds
  const [view, setView] = useState('active');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [mechanicFilter, setMechanicFilter] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [cities, setCities] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [list, setList] = useState({ rows: [], total: 0 });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [openLead, setOpenLead] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [reasonLead, setReasonLead] = useState(null);

  const buildParams = useCallback((extra = {}) => {
    const p = { page: 1, page_size: 200, status_group: view, ...extra };
    if (statusFilter) p.status = statusFilter;
    if (search.trim()) p.search = search.trim();
    if (cityFilter) p.city = cityFilter;
    if (mechanicFilter) p.mechanic_id = mechanicFilter;
    if (datePreset && datePreset !== 'all' && datePreset !== 'custom') {
      p.date_preset = datePreset;
    }
    if (datePreset === 'custom') {
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo)   p.date_to   = dateTo;
    }
    return p;
  }, [view, statusFilter, search, cityFilter, mechanicFilter, datePreset, dateFrom, dateTo]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await serviceOpsApi.list(buildParams());
      setList(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load service ops');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchCounts = useCallback(async () => {
    try {
      // Counts respect the same date filter (but not status / search / city / mechanic)
      const p = {};
      if (datePreset && datePreset !== 'all' && datePreset !== 'custom') p.date_preset = datePreset;
      if (datePreset === 'custom') {
        if (dateFrom) p.date_from = dateFrom;
        if (dateTo)   p.date_to   = dateTo;
      }
      const r = await serviceOpsApi.counts(p);
      setCounts(r.data);
    } catch {/* silent */}
  }, [datePreset, dateFrom, dateTo]);

  // Initial loads
  useEffect(() => {
    citiesApi.getNames()
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.cities || []);
        const names = arr.map((x) => (typeof x === 'string' ? x : x.name)).filter(Boolean);
        setCities([...new Set(names)].sort());
      })
      .catch(() => {});
    serviceOpsApi.mechanics()
      .then((r) => setMechanics(r.data?.mechanics || []))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchList(); fetchCounts(); }, [fetchList, fetchCounts]);

  // KPI cards + filter options for the active view and the Cancelled &
  // Refunds view both come from the server catalogue, so the modal dropdown
  // can never again set a status this page cannot show.
  const statCards = useMemo(() => {
    const carded = statusesIn(catalogue, view).filter((s) => s.card);
    const all = view === 'closed'
      ? { key: '', label: 'All Cancelled', icon: Ban, bg: 'bg-rose-500' }
      : { key: '', label: 'All Active', icon: ListChecks, bg: 'bg-violet-500' };
    if (!carded.length) return view === 'closed' ? [all] : OPS_STAT_CARDS;
    return [all, ...carded.map((s) => ({
      key: s.value,
      label: s.label,
      icon: CARD_ICONS[s.value] || ListChecks,
      bg: TONE_SOLID[s.tone] || 'bg-slate-500',
    }))];
  }, [catalogue, view]);

  const statusOptions = useMemo(() => {
    const opts = statusesIn(catalogue, view).filter((s) => s.filterable);
    if (!opts.length) return view === 'closed' ? [] : STATUS_FILTER_OPTIONS;
    return opts.map((s) => ({ key: s.value, label: s.label }));
  }, [catalogue, view]);

  const switchView = (v) => {
    if (v === view) return;
    setView(v);
    setStatusFilter('');   // a closed status in the active view matches nothing
  };

  const resetFilters = () => {
    setStatusFilter(''); setCityFilter(''); setMechanicFilter('');
    setDatePreset('all'); setDateFrom(''); setDateTo('');
    setSearch('');
  };

  const handleAssign = async (lead, mechanicId) => {
    if (!mechanicId) return;
    setMovingId(lead.id);
    const mech = mechanics.find((m) => m.id === mechanicId);
    try {
      await serviceOpsApi.assignMechanic(lead.id, { mechanic_id: mechanicId });
      toast.success(
        lead.status === 'BOOKED'
          ? `Assigned to ${mech?.name} → moved to Assigned`
          : `Re-assigned to ${mech?.name}`
      );
      fetchList();
      fetchCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    } finally {
      setMovingId(null);
    }
  };

  const handleAdvance = async (lead, newStatus) => {
    setMovingId(lead.id);
    try {
      await serviceOpsApi.updateStatus(lead.id, { status: newStatus });
      toast.success(`Moved → ${STATUS_LABEL[newStatus] || newStatus}`);
      fetchList();
      fetchCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Move failed');
    } finally {
      setMovingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="service-ops-page">
      <div className="max-w-[1400px] mx-auto px-6 pt-8 pb-6 space-y-6">

        {/* Title row */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Service Ops</h1>
            <p className="text-sm text-gray-500 mt-1">
              {view === 'closed'
                ? 'Cancelled, mechanic-rejected and refund-requested jobs — with what we collected'
                : 'Dispatch mechanics and track service jobs through completion'}
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1" data-testid="ops-view-toggle">
            {[
              { key: 'active', label: 'Active Queue', icon: ListChecks },
              { key: 'closed', label: 'Cancelled & Refunds', icon: Ban },
            ].map((t) => {
              const Icon = t.icon;
              const on = view === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => switchView(t.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    on ? (t.key === 'closed' ? 'bg-rose-600 text-white' : 'bg-blue-600 text-white')
                       : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`ops-view-${t.key}`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                  {t.key === 'closed' && counts.CLOSED_TOTAL > 0 && (
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${on ? 'bg-white/20' : 'bg-rose-100 text-rose-700'}`}>
                      {counts.CLOSED_TOTAL}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Date range card */}
        <Card className="px-5 py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Calendar className="h-4 w-4 text-gray-400" />
              Date Range:
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {DATE_PRESETS.map((d) => (
                <button
                  key={d.key}
                  onClick={() => {
                    setDatePreset(d.key);
                    if (d.key !== 'custom') { setDateFrom(''); setDateTo(''); }
                  }}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                    datePreset === d.key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                  data-testid={`ops-date-preset-${d.key}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            {datePreset === 'custom' && (
              <div className="flex items-center gap-2 ml-2">
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                       className="h-9 w-40" data-testid="ops-filter-date-from" />
                <span className="text-gray-400">→</span>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                       className="h-9 w-40" data-testid="ops-filter-date-to" />
              </div>
            )}
          </div>
        </Card>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {statCards.map((s) => {
            const isActive = (s.key === '' && !statusFilter) || statusFilter === s.key;
            const count = s.key === ''
              ? (view === 'closed' ? (counts.CLOSED_TOTAL ?? 0) : (counts.TOTAL ?? 0))
              : (counts[s.key] ?? 0);
            const Icon = s.icon;
            return (
              <button
                key={s.key || 'all'}
                onClick={() => setStatusFilter(s.key)}
                className={`bg-white rounded-xl border p-4 text-left transition-all hover:shadow-md ${
                  isActive ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'
                }`}
                data-testid={`ops-stat-card-${s.key || 'all'}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.bg}`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-2xl font-bold text-gray-900 leading-none">{count}</div>
                    <div className="text-xs text-gray-600 mt-1.5 truncate">{s.label}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        <Card className="px-4 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, mobile, RC number, or package…"
                className="pl-9 h-10 border-gray-200"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchList()}
                data-testid="ops-search"
              />
            </div>

            <Select value={mechanicFilter || '__all__'}
                    onValueChange={(v) => setMechanicFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-10 w-44" data-testid="ops-filter-mechanic">
                <SelectValue placeholder="All Mechanics" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Mechanics</SelectItem>
                {mechanics.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter || '__all__'}
                    onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-10 w-40" data-testid="ops-filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Status</SelectItem>
                {statusOptions.map(s => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={cityFilter || '__all__'}
                    onValueChange={(v) => setCityFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-10 w-40" data-testid="ops-filter-city">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all__">All Cities</SelectItem>
                {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="lg"
              className="h-10 gap-2"
              onClick={resetFilters}
              data-testid="ops-filter-clear"
            >
              <X className="h-4 w-4" /> Reset
            </Button>
          </div>
        </Card>

        {/* Data table */}
        {view === 'closed' ? (
          <CancelledRefundsTable
            rows={list.rows}
            total={list.total}
            loading={loading}
            catalogue={catalogue}
            onOpenLead={(lead) => { setOpenLeadId(lead.id); setOpenLead(lead); }}
            onAddReason={(lead) => setReasonLead(lead)}
          />
        ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="service-ops-table">
              <thead className="bg-gray-50 border-b text-left">
                <tr>
                  <th className="w-8 px-3 py-3"></th>
                  <th className="px-3 py-3 font-medium text-gray-700">Lead date/time</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Customer</th>
                  <th className="px-3 py-3 font-medium text-gray-700">City / Service location</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Car details</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Package</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Slot</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Mechanic</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {!loading && list.rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-gray-500 py-12">
                      <ClipboardCheck className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                      <div className="font-medium">No service jobs in the queue</div>
                      <div className="text-xs mt-1">Booked service leads will appear here for dispatch.</div>
                    </td>
                  </tr>
                )}

                {list.rows.map((lead) => {
                  const veh = lead.vehicle || {};
                  return (
                    <tr
                      key={lead.id}
                      className="border-b hover:bg-blue-50/40 cursor-pointer"
                      onClick={() => { setOpenLeadId(lead.id); setOpenLead(lead); }}
                      data-testid={`ops-row-${lead.id}`}
                    >
                      <td className="px-3 py-3 text-gray-400">
                        <ChevronRight className="h-4 w-4" />
                      </td>
                        <td className="px-3 py-3 align-top whitespace-nowrap">
                          <div className="font-medium text-gray-900">{fmtDateTime(lead.created_at)}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{timeAgo(lead.created_at)}</div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="font-semibold text-gray-900">{lead.customer_name || '—'}</div>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                            <Phone className="h-3 w-3" />{lead.mobile}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="flex items-center gap-1 text-gray-700">
                            <MapPin className="h-3.5 w-3.5 text-rose-500" />
                            <span className="font-medium">{lead.city || '—'}</span>
                          </div>
                          {lead.address && (
                            <div className="text-xs text-blue-600 mt-0.5 truncate max-w-[200px]" title={lead.address}>
                              {lead.address}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="font-medium text-gray-900">
                            {[veh.brand, veh.model].filter(Boolean).join(' ') || '—'}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {[veh.model || veh.body_class, veh.fuel_type].filter(Boolean).join(' · ')}
                          </div>
                          {veh.rc_number && (
                            <div className="text-[10px] font-mono uppercase text-gray-400 mt-0.5">{veh.rc_number}</div>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="font-medium text-gray-900">{lead.package_name || '—'}</div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {lead.quoted_price != null && (
                              <>Quoted <span className="font-semibold">{fmtINR(lead.quoted_price)}</span></>
                            )}
                            {lead.final_price != null && (
                              <span className="text-emerald-600 ml-1">Final <span className="font-semibold">{fmtINR(lead.final_price)}</span></span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top whitespace-nowrap">
                          <div className="text-gray-900">{fmtSlot(lead.confirmed_slot || lead.preferred_slot)}</div>
                          <div className="text-[10px] uppercase tracking-wide text-gray-400 mt-0.5">
                            {lead.confirmed_slot ? 'Confirmed' : 'Preferred'}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          {lead.assigned_mechanic_name ? (
                            <div>
                              <div className="text-gray-900 flex items-center gap-1">
                                <Wrench className="h-3 w-3 text-cyan-500" />
                                {lead.assigned_mechanic_name}
                              </div>
                              {lead.assigned_mechanic_mobile && (
                                <div className="text-xs text-gray-500 mt-0.5">{lead.assigned_mechanic_mobile}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs italic text-gray-400">Unassigned</span>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <Badge className={`${statusBadge(catalogue, lead.status)} border text-[10px] font-bold tracking-wide`}>
                            {statusLabel(catalogue, lead.status)}
                          </Badge>
                        </td>
                      </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {loading && (
            <div className="flex items-center justify-center py-6 text-sm text-gray-500 gap-2 border-t">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
        </Card>
        )}

        {/* Footer count */}
        <div className="text-xs text-gray-500 text-right">
          Showing {list.rows.length} of {list.total} jobs
        </div>
      </div>

      {/* ── Detail modal (May 2026 rehab): tabbed layout with header strip,
           spare parts, additional payments, invoice generation. */}
      <ServiceOpsDetailModal
        open={!!openLeadId}
        onClose={() => { setOpenLeadId(null); setOpenLead(null); }}
        leadId={openLeadId}
        initialLead={openLead}
        mechanics={mechanics}
        user={user}
        onChanged={() => { fetchList(); fetchCounts(); }}
      />

      {/* Retrospective reason entry for already-cancelled jobs */}
      <AddReasonDialog
        open={!!reasonLead}
        lead={reasonLead}
        onClose={() => setReasonLead(null)}
        onSaved={() => { fetchList(); fetchCounts(); }}
      />
    </div>
  );
}

